/**
 * ARCADE — le lanceur de mini-jeux. Liste les jeux + met en avant un « JEU DU JOUR »
 * qui tourne chaque jour (levier nouveauté pour les enfants TDAH). Ajouter un jeu =
 * l'ajouter à GAMES + une branche de rendu. Thémé auto (variables --kid-*).
 */
import { useState } from "react";
import MemoryGame from "./MemoryGame";
import CatchGame from "./CatchGame";
import RunnerGame from "./RunnerGame";
import TapGame from "./TapGame";

type GameId = "memory" | "catch" | "runner" | "tap";
const GAMES: { id: GameId; title: string; emoji: string; desc: string }[] = [
  { id: "memory", title: "Memory des créatures", emoji: "🃏", desc: "Retrouve les paires de créatures" },
  { id: "catch",  title: "Attrape !",            emoji: "🧺", desc: "Attrape les bons, évite les bombes" },
  { id: "runner", title: "Coureur",               emoji: "🏃", desc: "Saute par-dessus les obstacles !" },
  { id: "tap",    title: "Tape la créature !",    emoji: "🕳️", desc: "Tape-les quand elles surgissent !" },
];

function gameOfDay(): GameId {
  const d = new Date();
  const doy = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000);
  return GAMES[doy % GAMES.length].id;
}

export default function MiniGames({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [play, setPlay] = useState<GameId | null>(null);
  const featured = gameOfDay();

  if (play === "memory") return <MemoryGame apiBase={apiBase} onClose={() => setPlay(null)} />;
  if (play === "catch")  return <CatchGame  apiBase={apiBase} onClose={() => setPlay(null)} />;
  if (play === "runner") return <RunnerGame apiBase={apiBase} onClose={() => setPlay(null)} />;
  if (play === "tap")    return <TapGame    apiBase={apiBase} onClose={() => setPlay(null)} />;

  const feat = GAMES.find((g) => g.id === featured)!;

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>🎮 SALLE DE JEUX</div>
        <div style={{ width: 40 }} />
      </div>

      <div style={S.scroll}>
        {/* Jeu du jour */}
        <div style={S.dayLabel}>⭐ LE JEU DU JOUR</div>
        <button style={S.featCard} onClick={() => setPlay(feat.id)}>
          <span style={{ fontSize: 46 }}>{feat.emoji}</span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <span style={S.featTitle}>{feat.title}</span>
            <span style={S.featDesc}>{feat.desc}</span>
            <span style={S.playNow}>JOUER ▸</span>
          </span>
        </button>

        {/* Tous les jeux */}
        <div style={S.allLabel}>TOUS LES JEUX</div>
        <div style={S.grid}>
          {GAMES.map((g) => (
            <button key={g.id} style={S.gameCard} onClick={() => setPlay(g.id)}>
              <span style={{ fontSize: 34 }}>{g.emoji}</span>
              <span style={S.gameTitle}>{g.title}</span>
              {g.id === featured && <span style={S.badgeDay}>DU JOUR</span>}
            </button>
          ))}
        </div>
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 61, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em", fontSize: 15, color: "var(--kid-cyan)" },
  scroll: { flex: 1, overflowY: "auto", padding: "16px" },
  dayLabel: { fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em", fontSize: 12, color: "#ffd43b", marginBottom: 10 },
  featCard: { width: "100%", display: "flex", alignItems: "center", gap: 16, textAlign: "left", padding: "18px", borderRadius: 20, border: "1.5px solid #ffd43b", background: "linear-gradient(135deg, rgba(255,212,59,0.12), rgba(255,212,59,0.03))", cursor: "pointer" },
  featTitle: { fontSize: 19, fontWeight: 800 },
  featDesc: { fontSize: 13, color: "var(--kid-text-3)", marginTop: 2 },
  playNow: { marginTop: 8, color: "#ffd43b", fontWeight: 800, fontSize: 14, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.1em" },
  allLabel: { fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em", fontSize: 12, color: "var(--kid-text-3)", margin: "26px 0 12px" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  gameCard: { position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "20px 10px", borderRadius: 16, border: "1.5px solid var(--kid-cyan-40)", background: "rgba(255,255,255,0.03)", cursor: "pointer", color: "var(--kid-text)" },
  gameTitle: { fontSize: 13, fontWeight: 600, textAlign: "center", lineHeight: 1.2 },
  badgeDay: { position: "absolute", top: 8, right: 8, background: "#ffd43b", color: "#2a1d00", fontSize: 9, fontWeight: 800, borderRadius: 999, padding: "2px 6px", fontFamily: "var(--kid-font-mono)", letterSpacing: "0.06em" },
};
